import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Home, UserPlus, Users, LayoutGrid, X, ChevronRight, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TUTORIAL_KEY = 'landlord_tutorial_done';

interface Step {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: { label: string; path: string };
  highlight: string;
}

const STEPS: Step[] = [
  {
    icon: <Building2 size={32} className="text-primary" />,
    title: 'Create a Property',
    description: 'Start by adding your first property. A property is the building or estate you manage — give it a name, address, and photos.',
    action: { label: 'Add Property', path: '/landlord/properties/new' },
    highlight: 'Step 1 of 5',
  },
  {
    icon: <Home size={32} className="text-purple-500" />,
    title: 'Create a Unit',
    description: 'Once your property is set up, add units to it. Units are the individual rentable spaces — apartments, rooms, or offices.',
    action: { label: 'Go to Properties', path: '/landlord/properties' },
    highlight: 'Step 2 of 5',
  },
  {
    icon: <UserPlus size={32} className="text-blue-500" />,
    title: 'Invite a Tenant',
    description: "With a unit ready, invite a tenant by entering their phone number or email. They'll receive an invite to accept and join the unit.",
    action: { label: 'Manage Tenants', path: '/landlord/tenants' },
    highlight: 'Step 3 of 5',
  },
  {
    icon: <Users size={32} className="text-emerald-500" />,
    title: 'View Tenant Details',
    description: 'Once a tenant accepts the invite, view their profile, lease info, payment history, and maintenance requests from the Tenants section.',
    action: { label: 'View Tenants', path: '/landlord/tenants' },
    highlight: 'Step 4 of 5',
  },
  {
    icon: <LayoutGrid size={32} className="text-amber-500" />,
    title: 'Property Hub Actions',
    description: 'Tap any property card to open its hub. From there manage units, finances, vacancies, maintenance, utilities, staff, and more — all in one place.',
    action: { label: 'View Properties', path: '/landlord/properties' },
    highlight: 'Step 5 of 5',
  },
];

export const useLandlordTutorial = () => {
  const isDone = () => localStorage.getItem(TUTORIAL_KEY) === 'true';
  const markDone = () => localStorage.setItem(TUTORIAL_KEY, 'true');
  const reset = () => localStorage.removeItem(TUTORIAL_KEY);
  return { isDone, markDone, reset };
};

interface LandlordTutorialProps {
  onClose: () => void;
}

export const LandlordTutorial: React.FC<LandlordTutorialProps> = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const { markDone } = useLandlordTutorial();

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleSkip = () => {
    markDone();
    onClose();
  };

  const handleNext = () => {
    if (isLast) {
      markDone();
      onClose();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleAction = () => {
    if (current.action) {
      markDone();
      onClose();
      navigate(current.action.path);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.97 }}
          transition={{ duration: 0.22 }}
          className="w-full max-w-md bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <span className="text-xs font-semibold text-light-subtle dark:text-dark-subtle tracking-wide uppercase">
              {current.highlight}
            </span>
            <button
              onClick={handleSkip}
              className="p-1.5 rounded-lg text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              aria-label="Skip tutorial"
            >
              <X size={16} />
            </button>
          </div>

          {/* Step dots */}
          <div className="flex items-center gap-1.5 px-5 pb-4">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step
                    ? 'w-6 bg-primary dark:bg-golden'
                    : i < step
                    ? 'w-3 bg-primary/40 dark:bg-golden/40'
                    : 'w-3 bg-light-border dark:bg-dark-border'
                }`}
              />
            ))}
          </div>

          {/* Icon + Content */}
          <div className="px-5 pb-6 space-y-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border">
              {current.icon}
            </div>

            <div>
              <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-2">
                {current.title}
              </h2>
              <p className="text-sm text-light-subtle dark:text-dark-subtle leading-relaxed">
                {current.description}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              {step > 0 && (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg border border-light-border dark:border-dark-border text-sm text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                  <ChevronLeft size={15} />
                  Back
                </button>
              )}

              {current.action && (
                <button
                  onClick={handleAction}
                  className="flex-1 px-4 py-2 rounded-lg border border-primary dark:border-golden text-sm font-medium text-primary dark:text-golden hover:bg-primary/5 dark:hover:bg-golden/5 transition-colors"
                >
                  {current.action.label}
                </button>
              )}

              <button
                onClick={handleNext}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary dark:bg-golden text-white text-sm font-semibold hover:opacity-90 transition-opacity ml-auto"
              >
                {isLast ? 'Get Started' : 'Next'}
                {!isLast && <ChevronRight size={15} />}
              </button>
            </div>

            <button
              onClick={handleSkip}
              className="w-full text-center text-xs text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text transition-colors pt-1"
            >
              Skip tutorial
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
